import { useState } from "react";
import {
  Box,
  FormControlLabel,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { ValidationError } from "yup";

import type {
  S132Attempt,
  S132Record,
  WithdrawS132AttemptRequest,
} from "@thalamos/aspire-common";
import {
  S132WithdrawalReason,
  withdrawS132AttemptSchema,
} from "@thalamos/aspire-common";

import { s132WithdrawalReasonText } from "./S132StatusPill.js";
import {
  Button,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { ukLocalFormatDateTime } from "~/util.js";

export type WithdrawS132AttemptDialogProps = {
  attempt: S132Attempt;
  onClose: () => void;
  onWithdraw: (request: WithdrawS132AttemptRequest) => Promise<S132Record>;
  onWithdrawn: (record: S132Record) => void;
};

const label = { fontWeight: 600, fontSize: "0.85rem", mb: 0.75 };

/** Withdraws an entry entered in error, which users are offered as deleting it. It stays in the record, marked as withdrawn. */
export function WithdrawS132AttemptDialog(
  props: WithdrawS132AttemptDialogProps,
) {
  const [reason, setReason] = useState<S132WithdrawalReason | undefined>();
  const [detail, setDetail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const withdraw = async () => {
    setError(null);
    // Ask for the reason before the explanation, in the order they appear.
    if (!reason) {
      setError("Say why this entry is being deleted");
      return;
    }
    const request: WithdrawS132AttemptRequest = { reason, detail };
    try {
      await withdrawS132AttemptSchema.validate(request);
    } catch (e) {
      setError(
        e instanceof ValidationError
          ? (e.errors[0] ?? e.message)
          : "Something went wrong. Try again.",
      );
      return;
    }

    setIsSaving(true);
    try {
      props.onWithdrawn(await props.onWithdraw(request));
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : "The entry could not be deleted. Try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PopupDialog open onClose={props.onClose} maxWidth="sm">
      <PopupDialogTitle
        closeDialog={props.onClose}
        titleText="Delete an entry"
      />
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <Typography
          sx={{
            backgroundColor: "#FFF3CA",
            border: "1px solid #E1B205",
            borderRadius: "4px",
            padding: "8px 12px",
            fontSize: "0.85rem",
            color: "#6B4E00",
          }}
        >
          {`You're deleting the attempt from ${ukLocalFormatDateTime(props.attempt.occurredAt)}, recorded by ${props.attempt.recordedByName ?? "another user"}. It will stop counting towards the status, but stays in the record as deleted, with your name and reason. This can't be undone.`}
        </Typography>

        <Box>
          <Typography sx={label} id="s132-withdraw-reason">
            Why is it being deleted?
          </Typography>
          <RadioGroup
            aria-labelledby="s132-withdraw-reason"
            value={reason ?? ""}
            onChange={(e) => {
              setReason(e.target.value as S132WithdrawalReason);
            }}
          >
            {Object.values(S132WithdrawalReason).map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={s132WithdrawalReasonText(value)}
              />
            ))}
          </RadioGroup>
          {reason === S132WithdrawalReason.WrongPatient && (
            <Typography sx={{ fontSize: "0.75rem", color: "#7B7D89" }}>
              {
                "The entry's reason and notes will be hidden in this patient's record. Your explanation stays visible, so don't include the other patient's details. Record the attempt again against the right patient."
              }
            </Typography>
          )}
        </Box>

        <MuiTextField
          fullWidth
          multiline
          minRows={2}
          name="detail"
          label="Explain why"
          value={detail}
          onChange={(e) => {
            setDetail(e.target.value);
          }}
        />

        {error && (
          <Typography
            role="alert"
            sx={{ color: "#C74418", fontSize: "0.85rem" }}
          >
            {error}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button label="Cancel" outlined onClick={props.onClose} />
        <Button
          label="Delete entry"
          color="error"
          disabled={isSaving}
          onClick={() => {
            void withdraw();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
